import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Leaf, Users, Map, Award, Plane, ArrowRight, X, Tag, Snowflake, Lightbulb, TreePine, Ghost, Sparkles, Star } from "lucide-react";
import { useBooking } from "../../context/BookingContext";
import { useSiteData } from "../../context/SiteData";
import { optImg } from "../../lib/img";

const HIGHLIGHT_ICONS = [Map, Users, Award, Plane];

const REDUCED = typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

const CORNER_ICONS = { trees: TreePine, pumpkins: Ghost, star: Star, snowflake: Snowflake, lights: Lightbulb, leaves: Leaf };

const usePage = () => {
  const get = () => {
    if (typeof window === "undefined") return "home";
    const path = window.location.pathname;
    return path === "/" || /^\/(en|de|fr|ar)\/?$/.test(path) ? "home" : "landing";
  };
  const [page, setPage] = useState(get);
  useEffect(() => {
    const on = () => setPage(get());
    window.addEventListener("popstate", on);
    window.addEventListener("pushstate", on);
    const orig = history.pushState;
    history.pushState = function (...a) { const r = orig.apply(this, a); on(); return r; };
    return () => { window.removeEventListener("popstate", on); history.pushState = orig; };
  }, []);
  return page;
};

const useMobile = () => {
  const [mobile, setMobile] = useState(typeof window !== "undefined" && window.innerWidth < 640);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 639px)");
    const on = () => setMobile(mq.matches);
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);
  return mobile;
};

const effectOn = (theme, kind, page) => {
  const cfg = theme?.design?.effects?.[kind];
  if (cfg && (cfg.home || cfg.landing)) return !!cfg[page];
  return !!theme?.decorations?.[kind]; // legacy fallback
};

/* ---------------- announcement strip ---------------- */
export const SeasonalTopbar = () => {
  const { theme } = useSiteData();
  if (!theme?.announcement) return null;
  return (
    <div data-testid="seasonal-topbar" className="bg-[#0b132b] text-white text-center px-4 py-2.5">
      <p className="text-xs sm:text-sm font-semibold tracking-wide flex items-center justify-center gap-2 flex-wrap">
        <Sparkles className="w-4 h-4 text-[var(--seasonal-accent,var(--brand))] shrink-0" />
        {theme.announcement}
        <a
          href="#seasonal-offer"
          data-testid="seasonal-topbar-link"
          className="underline underline-offset-4 decoration-[var(--seasonal-accent,var(--brand))] hover:text-[var(--seasonal-accent,var(--brand))] transition-colors"
        >
          See the offer
        </a>
      </p>
    </div>
  );
};

/* ---------------- promo banner section ---------------- */
export const SeasonalBanner = ({ forcedTheme = null, page = "home" }) => {
  const { theme: ctxTheme } = useSiteData();
  const { openBooking } = useBooking();
  const theme = forcedTheme || ctxTheme;
  if (!theme?.headline) return null;
  if (!theme.pages?.includes(page)) return null;
  const highlights = theme.highlights?.length
    ? theme.highlights
    : ["Private tours", "Family experiences", "Local driver-guides", "Airport & hotel pickup"];
  return (
    <section id="seasonal-offer" data-testid="seasonal-banner" className="py-16 sm:py-20">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-14 items-center">
          <div>
            <p className="text-xs font-bold uppercase tracking-widest text-[var(--seasonal-accent,var(--brand))]" data-testid="seasonal-eyebrow">
              {theme.eyebrow || theme.name}
            </p>
            <h2 className="mt-4 font-display font-bold text-4xl sm:text-5xl text-[#0b132b] leading-[1.08]" data-testid="seasonal-heading">
              {theme.headline}
            </h2>
            <p className="mt-5 text-slate-600 leading-relaxed max-w-xl">{theme.description}</p>
            <div className="mt-7 grid grid-cols-2 gap-3 max-w-md">
              {highlights.slice(0, 4).map((label, i) => {
                const Icon = HIGHLIGHT_ICONS[i % HIGHLIGHT_ICONS.length];
                return (
                  <div key={label} className="flex items-center gap-2.5">
                    <span className="w-9 h-9 rounded-xl bg-[var(--seasonal-soft,var(--brand-soft))] flex items-center justify-center shrink-0">
                      <Icon className="w-4 h-4 text-[var(--seasonal-accent,var(--brand))]" />
                    </span>
                    <span className="text-sm font-semibold text-[#0b132b]">{label}</span>
                  </div>
                );
              })}
            </div>
            {theme.button_text && (
              <button
                type="button"
                onClick={() => openBooking({ mode: "tour" })}
                data-testid="seasonal-book-button"
                className="mt-8 inline-flex items-center gap-2 rounded-full bg-[var(--seasonal-accent,var(--brand))] text-white text-sm font-bold px-7 py-3.5 shadow-lg shadow-[var(--seasonal-accent,var(--brand))]/30 hover:brightness-90 hover:gap-3 transition-all"
              >
                {theme.button_text} <ArrowRight className="w-4 h-4" />
              </button>
            )}
            {theme.promo_code && (
              <p className="mt-4 inline-flex items-center gap-2 rounded-full border-2 border-dashed border-[var(--seasonal-accent,var(--brand))]/40 bg-[var(--seasonal-soft,var(--brand-soft))] px-4 py-2 text-sm font-bold text-[var(--seasonal-accent,var(--brand))]" data-testid="seasonal-promo-code">
                <Tag className="w-4 h-4" /> Use code {theme.promo_code}
              </p>
            )}
          </div>
          {theme.image && (
            <div className="relative rounded-[2.5rem] overflow-hidden h-[clamp(280px,45vw,480px)] shadow-[0_40px_80px_-30px_rgba(11,19,43,0.35)]" data-testid="seasonal-image-frame">
              <img
                src={optImg(theme.image_mobile && typeof window !== "undefined" && window.innerWidth < 640 ? theme.image_mobile : theme.image, 1200)}
                alt={theme.headline}
                loading="lazy"
                decoding="async"
                className="w-full h-full object-cover"
                data-testid="seasonal-image"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0b132b]/25 via-transparent to-transparent" />
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

/* ---------------- promotional popup (once per session) ---------------- */
export const SeasonalPopup = () => {
  const { theme } = useSiteData();
  const { openBooking } = useBooking();
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (!theme?.popup || REDUCED) return;
    if (sessionStorage.getItem(`seasonal-popup-${theme.id}`)) return;
    const t = setTimeout(() => setShow(true), 2500);
    return () => clearTimeout(t);
  }, [theme]);

  if (!theme?.popup) return null;
  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-40 flex items-end sm:items-center justify-center p-4 bg-[#0b132b]/40"
          onClick={() => setShow(false)}
          data-testid="seasonal-popup"
        >
          <motion.div
            initial={{ y: 40, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 40, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="relative w-full max-w-md rounded-3xl bg-white p-7 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              aria-label="Close"
              onClick={() => { sessionStorage.setItem(`seasonal-popup-${theme.id}`, "1"); setShow(false); }}
              className="absolute top-4 end-4 w-9 h-9 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center transition-colors"
              data-testid="seasonal-popup-close"
            >
              <X className="w-4 h-4 text-slate-600" />
            </button>
            <p className="text-xs font-bold uppercase tracking-widest text-[var(--seasonal-accent,var(--brand))]">{theme.eyebrow || theme.name}</p>
            <h3 className="mt-2 font-display font-bold text-2xl text-[#0b132b] leading-snug">{theme.headline}</h3>
            {theme.promo_code && (
              <p className="mt-3 inline-flex items-center gap-2 rounded-full border-2 border-dashed border-[var(--seasonal-accent,var(--brand))]/40 bg-[var(--seasonal-soft,var(--brand-soft))] px-4 py-2 text-sm font-bold text-[var(--seasonal-accent,var(--brand))]">
                <Tag className="w-4 h-4" /> Code: {theme.promo_code}
              </p>
            )}
            {theme.button_text && (
              <button
                type="button"
                onClick={() => { sessionStorage.setItem(`seasonal-popup-${theme.id}`, "1"); setShow(false); openBooking({ mode: "tour" }); }}
                data-testid="seasonal-popup-book"
                className="mt-5 w-full inline-flex items-center justify-center gap-2 rounded-full bg-[var(--seasonal-accent,var(--brand))] text-white text-sm font-bold px-6 py-3.5 shadow-lg shadow-[var(--seasonal-accent,var(--brand))]/30 hover:brightness-90 transition-colors"
              >
                {theme.button_text} <ArrowRight className="w-4 h-4" />
              </button>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

/* ---------------- decorative effects (CSS-only, lightweight) ---------------- */
const PARTICLE_META = {
  snow: { cls: "fx-snow", glyph: "❄" },
  leaves: { cls: "fx-leaf", glyph: "🍂" },
  fireworks: { cls: "fx-spark", glyph: "✦" },
  rain: { cls: "fx-rain", glyph: "" },
  fog: { cls: "fx-fog", glyph: "" },
  confetti: { cls: "fx-confetti", glyph: "" },
};
const DIRECTION_CLS = { down: "", up: "fx-rise", left: "fx-drift-left", right: "fx-drift-right" };
const BASE_DUR = 10; // seconds at speed 1

const Particles = ({ kind, cfg, mobile }) => {
  const meta = PARTICLE_META[kind];
  const [seeded, setSeeded] = useState([]);

  useEffect(() => {
    setSeeded(Array.from({ length: 200 }, (_, i) => ({
      left: (i * 37 + 13) % 100,
      delay: (i * 0.7) % 8,
      dur: BASE_DUR / Math.max(0.25, cfg.speed || 1),
      size: Math.max(4, (cfg.size || 14) * (0.6 + ((i * 7) % 40) / 60)),
      sway: ((i * 29) % 40) - 20,
    })));
  }, [cfg.speed, cfg.size]);

  // Mobile devices get half the particles for smooth performance.
  let count = Math.min(200, Math.max(0, cfg.count ?? 30));
  if (mobile) count = Math.ceil(count / 2);
  const dirCls = DIRECTION_CLS[cfg.direction] || "";
  const isShape = ["rain", "fog", "confetti"].includes(kind);

  return (
    <>
      {seeded.slice(0, count).map((p, i) => (
        <span
          key={i}
          className={`fx-particle ${kind === "fireworks" ? "fx-spark" : ""} ${isShape ? meta.cls : ""} ${isShape && kind === "confetti" ? `fx-confetti-${i % 6}` : ""} ${dirCls}`}
          style={{
            left: `${p.left}%`,
            top: kind === "fireworks" && !dirCls ? `${5 + ((i * 13) % 45)}%` : undefined,
            animationDelay: `${p.delay}s`,
            animationDuration: `${p.dur}s`,
            fontSize: isShape ? undefined : `${p.size}px`,
            width: isShape ? (kind === "fog" ? p.size * 3 : p.size) : undefined,
            height: isShape ? (kind === "fog" ? p.size : kind === "rain" ? p.size * 1.6 : p.size) : undefined,
            opacity: cfg.opacity,
            "--sway": `${p.sway}px`,
          }}
        >
          {isShape ? "" : meta.glyph}
        </span>
      ))}
    </>
  );
};

/* Uploaded-image particle systems (falling/floating/drifting custom PNGs/GIFs) */
const CustomParticles = ({ cfg, mobile }) => {
  const [seeded, setSeeded] = useState([]);
  useEffect(() => {
    setSeeded(Array.from({ length: 120 }, (_, i) => ({
      left: (i * 41 + 9) % 100,
      delay: (i * 0.9) % 9,
      dur: BASE_DUR / Math.max(0.25, cfg.speed || 1),
      size: Math.max(8, (cfg.size || 24) * (0.7 + ((i * 11) % 40) / 80)),
      sway: ((i * 23) % 36) - 18,
    })));
  }, [cfg.speed, cfg.size]);

  let count = Math.min(120, Math.max(1, cfg.count ?? 12));
  if (mobile) count = Math.max(3, Math.ceil(count / 2));
  const dirCls = DIRECTION_CLS[cfg.direction] || "";

  return (
    <>
      {seeded.slice(0, count).map((p, i) => (
        <span
          key={i}
          className={`fx-particle ${dirCls}`}
          style={{ left: `${p.left}%`, animationDelay: `${p.delay}s`, animationDuration: `${p.dur}s`, opacity: cfg.opacity ?? 0.85, "--sway": `${p.sway}px` }}
        >
          <img
            src={cfg.image}
            alt=""
            loading="lazy"
            decoding="async"
            className={cfg.animation === "rotate" ? "fx-anim-rotate" : cfg.animation === "bounce" ? "fx-anim-bounce" : ""}
            style={{ width: p.size, height: p.size, objectFit: "contain" }}
          />
        </span>
      ))}
    </>
  );
};

/* ---------------- full-site theme background ---------------- */
export const ThemePageBackground = () => {
  const { theme } = useSiteData();
  const page = usePage();
  const d = theme?.design?.page;
  const spec = d ? { ...(d.all || {}), ...Object.fromEntries(Object.entries(d[page] || {}).filter(([, v]) => v !== "" && v !== 0)) } : {};
  const type = spec.type;
  useEffect(() => {
    document.body.classList.toggle("theme-bg-active", !!type);
    return () => document.body.classList.remove("theme-bg-active");
  }, [type]);
  if (!type) return null;
  const filterBits = [];
  if (spec.brightness && spec.brightness !== 1) filterBits.push(`brightness(${spec.brightness})`);
  if (spec.blur) filterBits.push(`blur(${spec.blur}px)`);
  return (
    <div className="theme-page-bg" data-testid="theme-page-bg" aria-hidden="true">
      {type === "color" && spec.color && <div className="absolute inset-0" style={{ background: spec.color, filter: filterBits.join(" ") || undefined }} />}
      {type === "gradient" && spec.gradient && <div className="absolute inset-0" style={{ background: spec.gradient, filter: filterBits.join(" ") || undefined }} />}
      {type === "image" && spec.image && (
        <img src={spec.image} alt="" loading="eager" decoding="async" style={{ objectPosition: spec.position || "center", filter: filterBits.join(" ") || undefined }} />
      )}
      {type === "video" && spec.video && (
        <video src={spec.video} autoPlay muted loop playsInline style={{ objectPosition: spec.position || "center", filter: filterBits.join(" ") || undefined }} />
      )}
      {spec.overlay > 0 && <div className="absolute inset-0 bg-black" style={{ opacity: spec.overlay }} />}
    </div>
  );
};

export const SeasonalEffects = () => {
  const { theme } = useSiteData();
  const page = usePage();
  const mobile = useMobile();

  if (!theme || REDUCED) return null;
  const design = theme.design || {};
  const fx = design.effects || {};
  const pcfg = design.particle_cfg || {};
  const anyOn = Object.keys(fx).some((k) => effectOn(theme, k, page));
  if (!anyOn && !(design.items || []).length && !(design.custom_particles || []).length) return null;

  const posOf = (item) => {
    const key = mobile ? "mobile" : typeof window !== "undefined" && window.innerWidth < 1024 ? "tablet" : "desktop";
    return item[key] || item.desktop || { x: 90, y: 90 };
  };

  return (
    <div
      className="pointer-events-none fixed inset-0 z-30 overflow-hidden"
      aria-hidden="true"
      data-testid="seasonal-effects"
      style={{
        "--seasonal-accent": design.accent_color || undefined,
        "--seasonal-soft": design.accent_color ? `${design.accent_color}14` : undefined,
      }}
    >
      {Object.keys(PARTICLE_META).map((kind) =>
        effectOn(theme, kind, page) ? <Particles key={kind} kind={kind} cfg={pcfg[kind] || {}} mobile={mobile} /> : null
      )}
      {(design.custom_particles || [])
        .filter((cp) => (cp.pages || ["home"]).includes(page) && cp.image)
        .map((cp) => <CustomParticles key={cp.id} cfg={cp} mobile={mobile} />)
      }
      {effectOn(theme, "lights", page) && (
        <div className="fx-lights" data-testid="fx-lights" style={{ opacity: design.bulbs?.opacity ?? 1 }}>
          {Array.from({ length: Math.min(60, design.bulbs?.count ?? 24) }, (_, i) => (
            <span
              key={i}
              className={`fx-bulb fx-bulb-${i % 4}`}
              style={{ animationDelay: `${(i % 6) * 0.3}s`, animationDuration: `${1.8 / Math.max(0.25, design.bulbs?.speed || 1)}s` }}
            />
          ))}
        </div>
      )}
      {effectOn(theme, "trees", page) && !design.items?.some((it) => it.kind === "trees") && (
        <>
          <TreePine className="fx-corner-icon bottom-4 start-4 text-emerald-700/70" data-testid="fx-trees" />
          <TreePine className="fx-corner-icon bottom-4 end-4 text-emerald-700/70 scale-x-[-1]" />
        </>
      )}
      {effectOn(theme, "pumpkins", page) && !design.items?.some((it) => it.kind === "pumpkins") && (
        <>
          <Ghost className="fx-corner-icon bottom-4 start-4 text-orange-500/70" data-testid="fx-pumpkins" />
          <Ghost className="fx-corner-icon bottom-4 end-4 text-orange-500/70 scale-x-[-1]" />
        </>
      )}
      {/* Draggable decorations placed in the Design Studio */}
      {(design.items || [])
        .filter((item) => (item.pages || ["home"]).includes(page))
        .map((item) => {
          const pos = posOf(item);
          const Icon = !item.image && !item.glyph ? CORNER_ICONS[item.kind] || Star : null;
          return (
            <div
              key={item.id}
              className={`fx-item ${item.animation && !REDUCED ? `fx-anim-${item.animation}` : ""}`}
              data-testid={`fx-item-${item.id}`}
              style={{
                left: `${pos.x}%`, top: `${pos.y}%`,
                opacity: item.opacity ?? 0.7,
                width: item.size || 40, height: item.size || 40,
                ...(item.rotation ? { transform: `translate(-50%,-50%) rotate(${item.rotation}deg)` } : {}),
              }}
            >
              {item.image ? (
                <img src={item.image} alt="" loading="lazy" decoding="async" className="w-full h-full object-contain" />
              ) : item.glyph ? (
                <span style={{ fontSize: (item.size || 40) * 0.85, lineHeight: 1, display: "block", textAlign: "center" }}>{item.glyph}</span>
              ) : (
                <Icon className="w-full h-full" style={{ color: item.color || design.accent_color || "currentColor" }} />
              )}
            </div>
          );
        })}
    </div>
  );
};
